import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { concat } from "@ember/helper";
import { on } from "@ember/modifier";
import { action } from "@ember/object";
import didInsert from "@ember/render-modifiers/modifiers/did-insert";
import { cancel } from "@ember/runloop";
import { service } from "@ember/service";
import { modifier } from "ember-modifier";
import { Promise } from "rsvp";
import MenuPanel from "discourse/components/menu-panel";
import PluginOutlet from "discourse/components/plugin-outlet";
import AdvancedButton from "discourse/components/search-menu/advanced-button";
import ClearButton from "discourse/components/search-menu/clear-button";
import Results from "discourse/components/search-menu/results";
import SearchTerm from "discourse/components/search-menu/search-term";
import lazyHash from "discourse/helpers/lazy-hash";
import { popupAjaxError } from "discourse/lib/ajax-error";
import { search as searchCategoryTag } from "discourse/lib/category-tag-search";
import discourseDebounce from "discourse/lib/debounce";
import { bind } from "discourse/lib/decorators";
import getURL from "discourse/lib/get-url";
import {
  isValidSearchTerm,
  searchForTerm,
  searchTermScopesToPMs,
  updateRecentSearches,
} from "discourse/lib/search";
import { applyValueTransformer } from "discourse/lib/transformer";
import DiscourseURL from "discourse/lib/url";
import userSearch from "discourse/lib/user-search";
import { and } from "discourse/truth-helpers";
import DButton from "discourse/ui-kit/d-button";
import dConcatClass from "discourse/ui-kit/helpers/d-concat-class";
import dLoadingSpinner from "discourse/ui-kit/helpers/d-loading-spinner";
import { CANCELLED_STATUS } from "discourse/ui-kit/modifiers/d-autocomplete";
import { i18n } from "discourse-i18n";

const CATEGORY_SLUG_REGEXP = /(\#[a-zA-Z0-9\-:]*)$/gi;
const USERNAME_REGEXP = /(\@[a-zA-Z0-9\-\_]*)$/gi;
const SUGGESTIONS_REGEXP = /(in:|status:|order:|:)([a-zA-Z]*)$/gi;
export const MODIFIER_REGEXP = /.*(\#|\@|:).*$/gi;
export const DEFAULT_TYPE_FILTER = "exclude_topics";

export default class SearchMenu extends Component {
  @service search;
  @service currentUser;
  @service siteSettings;
  @service appEvents;
  @service a11y;

  @tracked loading = false;
  @tracked isPMInboxCleared = false;
  @tracked typeFilter = DEFAULT_TYPE_FILTER;
  @tracked suggestionKeyword = false;
  @tracked suggestionResults = [];
  @tracked invalidTerm = false;
  @tracked menuPanelOpen = false;

  searchInputId = this.args.searchInputId ?? "search-term";
  closeWhenHidden = modifier((_element, [hidden]) => hidden && this.close());
  _debouncer = null;
  _activeSearch = null;

  willDestroy() {
    if (!this.args.inlineResults) {
      document.removeEventListener("mousedown", this.onDocumentPress);
      document.removeEventListener("touchend", this.onDocumentPress);
    }
    super.willDestroy(...arguments);
  }

  get searchInputPlaceholder() {
    return applyValueTransformer(
      "search-menu-input-placeholder",
      this.args.searchInputPlaceholder || "search.title",
      { location: this.args.location }
    );
  }

  get searchInputWrapperClasses() {
    const extra = applyValueTransformer(
      "search-menu-input-wrapper-classes",
      [],
      { location: this.args.location }
    );

    return ["search-input-wrapper", ...extra].join(" ");
  }

  get classNames() {
    const classes = ["search-menu-container"];

    if (!this.args.inlineResults) {
      classes.push("menu-panel-results");
    }

    if (this.loading) {
      classes.push("loading");
    }

    return classes.join(" ");
  }

  // The chip both shows and clears topic scoping; a consumer that offers scope
  // as a choice of its own says so there instead.
  // The shortcut into advanced search; a consumer that offers it somewhere of
  // its own can drop it from the input.
  get showAdvancedButton() {
    return applyValueTransformer("search-menu-advanced-button-enabled", true, {
      location: this.args.location,
    });
  }

  get showSearchContext() {
    return applyValueTransformer("search-menu-search-context-enabled", true, {
      location: this.args.location,
    });
  }

  get includesTopics() {
    return (
      !!this.search.results?.topics?.length ||
      this.typeFilter !== DEFAULT_TYPE_FILTER
    );
  }

  get searchContext() {
    if (this.search.inTopicContext || this.inPMInboxContext) {
      return this.search.searchContext;
    }

    return false;
  }

  get inPMInboxContext() {
    return (
      !this.isPMInboxCleared &&
      this.search.searchContext?.type === "private_messages"
    );
  }

  get isPMOnly() {
    return (
      this.inPMInboxContext ||
      searchTermScopesToPMs(this.search.activeGlobalSearchTerm)
    );
  }

  get displayMenuPanelResults() {
    if (this.args.inlineResults || this.args.hideResults) {
      return false;
    }

    return this.menuPanelOpen;
  }

  @bind
  setupEventListeners() {
    // We only need to register click events when the search menu is rendered outside of the header.
    // The header handles clicking outside.
    if (!this.args.inlineResults) {
      document.addEventListener("mousedown", this.onDocumentPress);
      document.addEventListener("touchend", this.onDocumentPress);
    }
  }

  @bind
  onDocumentPress(event) {
    if (!this.menuPanelOpen) {
      return;
    }

    if (!event.target.closest(".search-menu-container.menu-panel-results")) {
      this.close();
    }
  }

  @action
  onKeydown(event) {
    if (event.key === "Escape") {
      this.close();
      event.preventDefault();
      event.stopPropagation();
    }
  }

  @action
  close() {
    if (this.args?.onClose) {
      return this.args.onClose();
    }

    // We want to blur the search input when in stand-alone mode
    // so that when we focus on the search input again, the menu panel pops up
    document.getElementById(this.searchInputId)?.blur();
    this.menuPanelOpen = false;
  }

  @action
  open() {
    if (this.args.hideResults) {
      return;
    }

    if (!this.menuPanelOpen) {
      this.appEvents.trigger("search-menu:search_menu_opened");
    }
    this.menuPanelOpen = true;
  }

  @bind
  fullSearchUrl(opts) {
    let url = "/search";
    let params = new URLSearchParams();

    if (this.search.activeGlobalSearchTerm) {
      let q = this.search.activeGlobalSearchTerm;

      if (this.searchContext?.type === "topic") {
        q += ` topic:${this.searchContext.id}`;
      } else if (this.searchContext?.type === "private_messages") {
        q += " in:messages";
      }
      params.set("q", q);
    }
    if (opts?.expanded) {
      params.set("expanded", "true");
    }

    params = applyValueTransformer("search-menu-full-search-params", params, {
      location: this.args.location,
    });

    if (params.toString() !== "") {
      url = `${url}?${params}`;
    }
    return getURL(url);
  }

  @action
  openAdvancedSearch() {
    this.loading = false;
    const url = this.fullSearchUrl({ expanded: true });
    if (url) {
      DiscourseURL.routeTo(url);
    }
    this.close();
  }

  @bind
  clearSearch(e) {
    e.stopPropagation();
    e.preventDefault();
    this.search.activeGlobalSearchTerm = "";
    this.search.focusSearchInput();
    this.triggerSearch();
  }

  @action
  searchTermChanged(term, opts = {}) {
    this.typeFilter = opts.searchTopics ? null : DEFAULT_TYPE_FILTER;
    if (opts.setTopicContext) {
      this.search.inTopicContext = true;
    }
    if (opts.setPMInboxContext) {
      this.isPMInboxCleared = false;
    }
    this.search.activeGlobalSearchTerm = term;
    this.triggerSearch();
  }

  @action
  fullSearch() {
    this.loading = false;
    const url = this.fullSearchUrl();
    if (url) {
      DiscourseURL.routeTo(url);
    }
  }

  @action
  updateTypeFilter(value) {
    this.typeFilter = value;
  }

  @action
  clearPMInboxContext() {
    this.isPMInboxCleared = true;
  }

  @action
  clearTopicContext() {
    this.search.inTopicContext = false;
  }

  // for cancelling debounced search
  cancel() {
    if (this._activeSearch) {
      this._activeSearch.abort();
      this._activeSearch = null;
    }
  }

  async perform() {
    this.cancel();

    const matchSuggestions = this.matchesSuggestions();
    if (matchSuggestions) {
      this.search.noResults = true;
      this.search.results = {};
      this.loading = false;
      this.suggestionResults = [];

      if (matchSuggestions.type === "category") {
        const categorySearchTerm = matchSuggestions.categoriesMatch[0].replace(
          "#",
          ""
        );

        const categoryTagSearch = searchCategoryTag(
          categorySearchTerm,
          this.siteSettings
        );
        Promise.resolve(categoryTagSearch).then((results) => {
          if (results !== CANCELLED_STATUS) {
            this.suggestionResults = results;
            this.suggestionKeyword = "#";
          }
        });
      } else if (matchSuggestions.type === "username") {
        const userSearchTerm = matchSuggestions.usernamesMatch[0].replace(
          "@",
          ""
        );
        const opts = { includeGroups: true, limit: 6 };
        if (userSearchTerm.length > 0) {
          opts.term = userSearchTerm;
        } else {
          opts.lastSeenUsers = true;
        }

        userSearch(opts).then((result) => {
          if (result?.users?.length > 0) {
            this.suggestionResults = result.users;
            this.suggestionKeyword = "@";
          } else {
            this.search.noResults = true;
            this.suggestionKeyword = false;
          }
        });
      } else {
        this.suggestionKeyword = matchSuggestions[0];
      }
      return;
    }

    this.suggestionKeyword = false;
    const announceOutcome = this.typeFilter !== DEFAULT_TYPE_FILTER;

    if (!this.search.activeGlobalSearchTerm) {
      this.search.noResults = false;
      this.search.results = {};
      this.loading = false;
      this.invalidTerm = false;
    } else if (
      !isValidSearchTerm(this.search.activeGlobalSearchTerm, this.siteSettings)
    ) {
      this.search.noResults = true;
      this.search.results = {};
      this.loading = false;
      this.invalidTerm = true;

      if (announceOutcome) {
        this.a11y.announce(i18n("search.too_short"), "polite");
      }
    } else {
      this.loading = true;
      this.invalidTerm = false;

      const searchContext = this.searchContext;
      this._activeSearch = searchForTerm(this.search.activeGlobalSearchTerm, {
        typeFilter: this.typeFilter,
        fullSearchUrl: this.fullSearchUrl,
        searchContext,
      });

      this._activeSearch
        .then((results) => {
          if (
            results &&
            searchContext?.type === this.searchContext?.type &&
            searchContext?.id === this.searchContext?.id
          ) {
            this.search.noResults = results.resultTypes.length === 0;
            this.search.results = results;

            if (announceOutcome) {
              this.#announceResults(results);
            }
          }
        })
        .catch(popupAjaxError)
        .finally(() => {
          this.loading = false;
        });
    }
  }

  matchesSuggestions() {
    if (
      this.search.activeGlobalSearchTerm === undefined ||
      this.includesTopics
    ) {
      return false;
    }

    const term = this.search.activeGlobalSearchTerm.trim();
    const categoriesMatch = term.match(CATEGORY_SLUG_REGEXP);

    if (categoriesMatch) {
      return { type: "category", categoriesMatch };
    }

    const usernamesMatch = term.match(USERNAME_REGEXP);
    if (usernamesMatch) {
      return { type: "username", usernamesMatch };
    }

    const suggestionsMatch = term.match(SUGGESTIONS_REGEXP);
    if (suggestionsMatch) {
      return suggestionsMatch;
    }

    return false;
  }

  @action
  triggerSearch() {
    this.search.noResults = false;

    if (this.includesTopics) {
      if (this.search.contextType === "topic") {
        this.search.highlightTerm = this.search.activeGlobalSearchTerm;
      }
      this.loading = true;
      cancel(this._debouncer);
      this.perform();

      if (this.currentUser) {
        updateRecentSearches(
          this.currentUser,
          this.search.activeGlobalSearchTerm
        );
      }
    } else {
      this.loading = false;
      if (!this.search.inTopicContext) {
        this._debouncer = discourseDebounce(this, this.perform, 400);
      }
    }
  }

  #announceResults(results) {
    const count = results.resultTypes.reduce(
      (total, type) => total + type.results.length,
      0
    );

    this.a11y.announce(
      count
        ? i18n("search.results_announcement", { count })
        : i18n("search.no_results"),
      "polite"
    );
  }

  <template>
    {{! eslint-disable ember/template-no-invalid-interactive }}
    <div
      class={{this.classNames}}
      {{didInsert this.setupEventListeners}}
      {{this.closeWhenHidden @hideResults}}
      {{on "keydown" this.onKeydown}}
    >
      <div class={{this.searchInputWrapperClasses}}>
        <div
          class={{dConcatClass
            "search-input"
            (concat "search-input--" @location)
          }}
        >
          {{#if (and this.search.inTopicContext this.showSearchContext)}}
            <DButton
              class="btn-default btn-small search-context"
              @action={{this.clearTopicContext}}
              @icon="xmark"
              @label="search.in_this_topic"
              @title="search.in_this_topic_tooltip"
            />
          {{else if (and this.inPMInboxContext this.showSearchContext)}}
            <DButton
              class="btn-default btn-small search-context"
              @action={{this.clearPMInboxContext}}
              @icon="xmark"
              @label="search.in_messages"
              @title="search.in_messages_tooltip"
            />
          {{/if}}

          <PluginOutlet
            @name="search-menu-before-term-input"
            @outletArgs={{lazyHash openSearchMenu=this.open}}
          />

          <SearchTerm
            @autofocus={{@autofocusInput}}
            @clearPMInboxContext={{this.clearPMInboxContext}}
            @clearTopicContext={{this.clearTopicContext}}
            @closeSearchMenu={{this.close}}
            @fullSearch={{this.fullSearch}}
            @inputId={{this.searchInputId}}
            @inputPlaceholder={{this.searchInputPlaceholder}}
            @location={{@location}}
            @openSearchMenu={{this.open}}
            @searchTermChanged={{this.searchTermChanged}}
            @triggerSearch={{this.triggerSearch}}
            @typeFilter={{this.typeFilter}}
            @updateTypeFilter={{this.updateTypeFilter}}
          />

          {{#if this.loading}}
            <div class="searching">
              {{dLoadingSpinner}}
            </div>
          {{else}}
            <div class="searching">
              <PluginOutlet @name="search-menu-before-advanced-search" />
              {{#if this.search.activeGlobalSearchTerm}}
                <ClearButton @clearSearch={{this.clearSearch}} />
              {{/if}}
              {{#if this.showAdvancedButton}}
                <AdvancedButton
                  @openAdvancedSearch={{this.openAdvancedSearch}}
                />
              {{/if}}
            </div>
          {{/if}}
        </div>
      </div>

      {{#if @inlineResults}}
        <Results
          @clearPMInboxContext={{this.clearPMInboxContext}}
          @clearSearch={{this.clearSearch}}
          @clearTopicContext={{this.clearTopicContext}}
          @closeSearchMenu={{this.close}}
          @inPMInboxContext={{this.inPMInboxContext}}
          @invalidTerm={{this.invalidTerm}}
          @isPMOnly={{this.isPMOnly}}
          @loading={{this.loading}}
          @location={{@location}}
          @openAdvancedSearch={{this.openAdvancedSearch}}
          @searchInputId={{this.searchInputId}}
          @searchTermChanged={{this.searchTermChanged}}
          @searchTopics={{this.includesTopics}}
          @suggestionKeyword={{this.suggestionKeyword}}
          @suggestionResults={{this.suggestionResults}}
          @triggerSearch={{this.triggerSearch}}
          @updateTypeFilter={{this.updateTypeFilter}}
        />
      {{else if this.displayMenuPanelResults}}
        <MenuPanel class="search-menu-panel">
          <Results
            @clearPMInboxContext={{this.clearPMInboxContext}}
            @clearSearch={{this.clearSearch}}
            @clearTopicContext={{this.clearTopicContext}}
            @closeSearchMenu={{this.close}}
            @inPMInboxContext={{this.inPMInboxContext}}
            @invalidTerm={{this.invalidTerm}}
            @isPMOnly={{this.isPMOnly}}
            @loading={{this.loading}}
            @location={{@location}}
            @openAdvancedSearch={{this.openAdvancedSearch}}
            @searchInputId={{this.searchInputId}}
            @searchTermChanged={{this.searchTermChanged}}
            @searchTopics={{this.includesTopics}}
            @suggestionKeyword={{this.suggestionKeyword}}
            @suggestionResults={{this.suggestionResults}}
            @triggerSearch={{this.triggerSearch}}
            @updateTypeFilter={{this.updateTypeFilter}}
          />
        </MenuPanel>
      {{/if}}
    </div>
  </template>
}
